import { LoadingIndicator } from '../loading.jsx';
import xhr from '../xhr.js';
import { OAuth2AppInfo } from './app/info.jsx';
import { OAuth2AppScopeList } from './app/scope.jsx';
import { OAuth2Header } from './header/header.js';
import { OAuth2UserHeader } from './header/user.jsx';

const ERRORS = {
    'invalid_request': {
        description: 'The request is missing a required parameter, includes an invalid parameter value, includes a parameter more than once, or is otherwise malformed.',
    },
    'unauthorized_client': {
        description: 'The client is not authorized to request an authorisation code using this method.',
    },
    'access_denied': {
        description: 'The resource owner or authorization server denied the request.',
    },
    'unsupported_response_type': {
        description: 'The authorisation server does not support obtaining an authorisation code using this method.',
    },
    'invalid_scope': {
        description: 'The requested scope is invalid, unknown, or malformed.',
    },
    'server_error': {
        description: 'The authorisation server encountered an unexpected condition that prevented it from fulfilling the request.',
    },
    'temporarily_unavailable': {
        description: 'The authorization server is currently unable to handle the request due to a temporary overloading or maintenance of the server.',
    },
};

export async function initOAuth2Authorize() {
    const queryParams = new URLSearchParams(window.location.search);
    const loading = new LoadingIndicator({ element: '.js-loading', size: 2 });
    const header = new OAuth2Header;

    const fAuths = document.querySelector('.js-authorize-form');
    const eAuthsInfo = document.querySelector('.js-authorize-form-info');
    const eAuthsScope = document.querySelector('.js-authorize-form-scope');

    const dError = document.querySelector('.js-authorize-error');
    const dErrorText = dError?.querySelector('.js-authorize-error-text');

    let scope;
    let state;
    let redirectUri;
    let redirectUriRaw;

    const displayError = (error, description, documentation) => {
        if(redirectUri === undefined) {
            if(error in ERRORS) {
                const errInfo = ERRORS[error];
                description ??= errInfo.description;
            } else
                description = `An unknown error occurred: ${error}`;

            dErrorText.textContent = description;

            header.setSimpleData('error', 'An error occurred!');
            header.removeElement();

            loading.visible = false;
            fAuths.classList.add('hidden');
            dError.classList.remove('hidden');
            return;
        }

        const errorUri = new URL(redirectUri);
        errorUri.searchParams.set('error', error?.toString() ?? 'invalid_request');
        if(description)
            errorUri.searchParams.set('error_description', description.toString());
        if(documentation)
            errorUri.searchParams.set('error_uri', documentation.toString());
        if(state !== undefined)
            errorUri.searchParams.set('state', state.toString());

        window.location.assign(errorUri);
    };
    const translateError = (serverError, detail) => {
        if(serverError === 'auth')
            return displayError('access_denied');
        if(serverError === 'authorize')
            return displayError('server_error', 'Server was unable to complete authorisation.');
        if(serverError === 'challenge')
            return displayError('invalid_request', 'Code challenge is required for public clients.');
        if(serverError === 'client')
            return displayError('invalid_request', 'There is no application associated with the specified Client ID.');
        if(serverError === 'csrf')
            return displayError('invalid_request', 'Request verification failed.');
        if(serverError === 'format')
            return displayError('invalid_request', 'Redirect URI specified is not registered with this application.');
        if(serverError === 'length')
            return displayError('invalid_request', 'Code challenge length is not acceptable.');
        if(serverError === 'method')
            return displayError('invalid_request', 'Requested code challenge method is not supported.');
        if(serverError === 'resptype')
            return displayError('unsupported_response_type');
        if(serverError === 'required')
            return displayError('invalid_request', 'A registered redirect URI must be specified.');
        if(serverError === 'scope')
            return displayError('invalid_scope', detail === undefined ? undefined : `Requested scope "${detail.scope}" is ${detail.reason}.`);

        return displayError('invalid_request', `An unknown error occurred: ${serverError}.`);
    };

    if(queryParams.has('redirect_uri'))
        try {
            const qRedirectUriRaw = queryParams.get('redirect_uri');
            const qRedirectUri = new URL(qRedirectUriRaw);
            if(qRedirectUri.protocol !== 'https:' && qRedirectUri.hostname !== 'localhost')
                throw new Error('protocol must be https or hostname must be localhost');

            redirectUri = qRedirectUri;
            redirectUriRaw = qRedirectUriRaw;
        } catch(ex) {
            return displayError('invalid_request', 'Invalid redirect URI specified.');
        }

    if(queryParams.has('state')) {
        const qState = queryParams.get('state');

        if(qState.length > 1000)
            return displayError('invalid_request', 'State parameter may not be longer than 255 characters.');

        state = qState;
    }

    const responseTypeArr = (queryParams.get('response_type') ?? '').split(' ').sort();
    const responseTypeStr = responseTypeArr.join(' ');
    if(!['code', 'code id_token'].includes(responseTypeStr))
        return displayError('unsupported_response_type');

    let codeChallengeMethod;
    if(queryParams.has('code_challenge_method')) {
        codeChallengeMethod = queryParams.get('code_challenge_method');
        if(!['plain', 'S256'].includes(codeChallengeMethod))
            return translateError('method');
    }

    let codeChallenge;
    if(queryParams.has('code_challenge')) {
        codeChallenge = queryParams.get('code_challenge');
        if(codeChallengeMethod === 'S256') {
            if(codeChallenge.length !== 43)
                return displayError('invalid_request', 'Specified code challenge is not a valid SHA-256 hash.');
        } else {
            if(codeChallenge.length < 43)
                return displayError('invalid_request', 'Code challenge must be at least 43 characters long.');
            if(codeChallenge.length > 128)
                return displayError('invalid_request', 'Code challenge may not be longer than 128 characters.');

            codeChallengeMethod ??= 'plain';
        }
    } else if(codeChallengeMethod !== undefined) {
        return displayError('invalid_request', 'code_challenge must be specified.');
    }

    const resolveParams = new URLSearchParams;
    resolveParams.set('client', queryParams.get('client_id'));
    if(redirectUriRaw !== undefined)
        resolveParams.set('redirect', redirectUriRaw);
    if(queryParams.has('scope')) {
        scope = queryParams.get('scope');
        resolveParams.set('scope', scope);
    }

    try {
        const { body } = await xhr.get(`/oauth2/resolve-authorize-app?${resolveParams}`, { authed: true, csrf: true, type: 'json' });
        if(!body)
            throw 'authorisation resolve failed';
        if(typeof body.error === 'string') {
            if(body.error === 'auth') {
                window.location.assign(`/auth/login.php?oauth2=1&redirect=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`);
                return;
            }

            return translateError(body.error, body);
        }

        const userHeader = new OAuth2UserHeader(body.user);
        header.setElement(userHeader);

        const verifyAuthsRequest = async () => {
            const params = {
                client: queryParams.get('client_id'),
                rt: responseTypeStr,
            };
            if(codeChallenge !== undefined)
                params.cc = codeChallenge;
            if(codeChallengeMethod !== undefined)
                params.ccm = codeChallengeMethod;
            if(redirectUriRaw !== undefined)
                params.redirect = redirectUriRaw;
            if(scope !== undefined)
                params.scope = scope;

            try {
                const { body } = await xhr.post('/oauth2/authorize', { authed: true, csrf: true, type: 'json' }, params);
                if(!body)
                    throw 'authorisation failed';
                if(typeof body.error === 'string')
                    return translateError(body.error, body);

                const authorizeUri = new URL(body.redirect);
                authorizeUri.searchParams.set('code', body.code);
                if(body.id_token !== undefined)
                    authorizeUri.searchParams.set('id_token', body.id_token);
                if(state !== undefined)
                    authorizeUri.searchParams.set('state', state.toString());

                window.location.assign(authorizeUri);
            } catch(ex) {
                console.error(ex);
                translateError('authorize');
            }
        };

        if(body.app.trusted && body.user.guise === undefined) {
            if(userHeader)
                userHeader.guiseVisible = false;

            verifyAuthsRequest();
            return;
        }

        eAuthsInfo.replaceWith(new OAuth2AppInfo(body.app).element);
        eAuthsScope.replaceWith(new OAuth2AppScopeList(body.scope).element);

        fAuths.onsubmit = ev => {
            ev.preventDefault();

            loading.visible = true;
            fAuths.classList.add('hidden');

            if(userHeader)
                userHeader.guiseVisible = false;

            if(ev.submitter?.value === 'yes')
                verifyAuthsRequest();
            else
                displayError('access_denied');
        };

        loading.visible = false;
        fAuths.classList.remove('hidden');
    } catch(ex) {
        console.error(ex);
        displayError('server_error', 'Server was unable to respond to the client info request.');
    }
};
